import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { logout as apiLogout, setAccessToken, tryAutoLogin, type User } from '../api/client';

type Ctx = {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  logout: () => Promise<void>;
  setSession: (p: { accessToken: string; user: User }) => void;
};

const AuthCtx = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tryAutoLogin().then((r) => {
      if (r?.user) setUser(r.user);
      setLoading(false);
    });
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      user,
      loading,
      login: async (identifier, password) => {
        const r = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ identifier, password }),
        });
        if (!r.ok) {
          return {
            ok: false,
            error:
              r.status === 401
                ? 'Неверный логин или пароль'
                : r.status === 429
                  ? 'Слишком много попыток, подождите несколько минут'
                  : 'Ошибка сервера',
          };
        }
        const data = (await r.json()) as { accessToken: string; user: User };
        setAccessToken(data.accessToken);
        setUser(data.user);
        return { ok: true };
      },
      logout: async () => {
        await apiLogout();
        setUser(null);
      },
      setSession: ({ accessToken, user: u }) => {
        setAccessToken(accessToken);
        setUser(u);
      },
    }),
    [user, loading],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): Ctx {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
